import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { ChatApiService } from '../../shared-services/chat-api.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DynamicEditTableComponent } from '../../chart-components/dynamic-edit-table/dynamic-edit-table.component';
import { StreamingTextComponent } from '../streaming-text/streaming-text.component';
import { environment } from '../../../../../my-app/src/environments/environment';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ImageOverlayComponent } from '../image-overlay/image-overlay.component';
import { ChatStateService } from '../../shared-services/chat-state.service';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

// Define interfaces locally for the component
interface Step {
  step: number;
  text: string;
  has_image: boolean;
  image_urls: string[];
}

interface Message {
  text: string;
  sender: 'user' | 'agent';
  files?: File[];
  type?: 'text' | 'table' | 'option' | 'json' | 'steps';
  data?: any;
  formattedText?: string;
  rawJson?: string;
  copied?: boolean;
  loading?: boolean;
  completed?: boolean;
  steps?: Step[];
  isStreaming?: boolean;
  action_type?: 'show_progress_bar';
  progress?: number;
  job_id?: string;
}

@Component({
  selector: 'app-chat-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatTooltipModule,
    DynamicEditTableComponent,
    StreamingTextComponent,
    ProgressBarComponent,
    ImageOverlayComponent
  ],
  templateUrl: './chat-popup.component.html',
  styleUrls: ['./chat-popup.component.scss']
})
export class ChatPopupComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('chatBody') private chatBody!: ElementRef;
  @ViewChild(DynamicEditTableComponent) private dynamicEditTable?: DynamicEditTableComponent;
  
  isChatOpen = false;
  messages: Message[] = [];
  currentMessage = '';
  isLoading = false;
  selectedFiles: File[] = [];
  isDarkTheme = false;
  selectedImageUrl: string | null = null;

  private subscriptions = new Subscription();
  private previousMessageCount = 0;

  constructor(
    private chatApiService: ChatApiService,
    public chatStateService: ChatStateService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.chatStateService.messages$.subscribe(messages => {
      this.messages = messages;
      this.scrollToBottom();
    }));
    this.subscriptions.add(this.chatStateService.isLoading$.subscribe(isLoading => this.isLoading = isLoading));
    this.subscriptions.add(this.chatStateService.selectedFiles$.subscribe(selectedFiles => this.selectedFiles = selectedFiles));
    
    // Subscribe to chatInput$ (already present)
  this.subscriptions.add(
    this.chatStateService.chatInput$.subscribe(input => {
      this.currentMessage = input;
    })
  );

  // NEW: subscribe to openChat$ to auto-open chat
  this.subscriptions.add(
    this.chatStateService.openChat$.subscribe(() => {
      this.isChatOpen = true;
      this.previousMessageCount = 0; // optional: force scroll
      this.scrollToBottom();

      // Optional: focus input field
      const inputElem = document.querySelector<HTMLInputElement>('input.chat-input');
      if (inputElem) inputElem.focus();
    })
  );

  if (!this.chatApiService.sessionId) {
    this.chatApiService.createSession().then(obs => this.subscriptions.add(obs.subscribe()));
  }
  }
  
  ngAfterViewChecked() {
    if (this.isChatOpen && this.messages.length !== this.previousMessageCount) {
      this.scrollToBottom();
      this.previousMessageCount = this.messages.length;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  toggleChat(): void {
    this.isChatOpen = !this.isChatOpen;
    if (this.isChatOpen) {
      this.previousMessageCount = 0; // Reset to force scroll on open
      this.scrollToBottom();
    }
  }

  scrollToBottom(): void {
    if (this.chatBody && this.chatBody.nativeElement) {
      setTimeout(() => this.chatBody.nativeElement.scrollTop = this.chatBody.nativeElement.scrollHeight, 0);
    }
  }
  
  openImageOverlay(imageUrl: string): void {
    this.selectedImageUrl = imageUrl;
  }

  closeImageOverlay(): void {
    this.selectedImageUrl = null;
  }

  getSafeImageUrl(relativeUrl: string): SafeUrl {
    const agentApiUrl = environment.agentApiUrl.trim().replace(/,$/, '');
    const fullUrl = agentApiUrl + relativeUrl;
    return this.sanitizer.bypassSecurityTrustUrl(fullUrl);
  }

  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;

    if (this.isDarkTheme) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }
  
  startNewSession(): void {
    this.chatStateService.clearAll();
  }

  sendMessageOnEnter(event: Event | KeyboardEvent): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  formatText(text: string): string {
    let formatted = text;
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/^-\s(.+)$/gm, '<li>$1</li>');
    formatted = formatted.replace(/((?:<li>.*?<\/li>\s*)+)/g, '<ul>$1</ul>');
    return formatted;
  }

  // sendMessage(): void {
  //   if ((!this.currentMessage.trim() && this.selectedFiles.length === 0) || this.isLoading) {
  //     return;
  //   }

  //   const formData = new FormData();

  //   let tableData: any[] | undefined;
  //   if (this.dynamicEditTable && this.dynamicEditTable.isTableEditable()) {
  //     tableData = this.dynamicEditTable.getTableData();
  //     const tableMessageIndex = this.messages.map(m => m.type).lastIndexOf('table');
  //     if (tableMessageIndex > -1) {
  //       const newMessages = [...this.messages];
  //       newMessages.splice(tableMessageIndex, 1);
  //       this.chatStateService.messages = newMessages;
  //     }
  //   }

  //   let jsonData: any | undefined;
  //   const jsonMessageIndex = this.messages.map(m => m.type).lastIndexOf('json');
  //   if (jsonMessageIndex > -1) {
  //     const jsonMessage = this.messages[jsonMessageIndex];
  //     try {
  //       if (jsonMessage.rawJson) {
  //         jsonData = JSON.parse(jsonMessage.rawJson);
  //       }
  //       const newMessages = [...this.messages];
  //       newMessages.splice(jsonMessageIndex, 1);
  //       this.chatStateService.messages = newMessages;
  //     } catch (e) {
  //       this.chatStateService.addMessage({ text: 'Error: Invalid JSON format. Fix before sending.', sender: 'agent' });
  //       return;
  //     }
  //   }

  //   const userMessage: Message = {
  //     text: this.currentMessage,
  //     sender: 'user',
  //     files: [...this.selectedFiles],
  //   };

  //   this.chatStateService.addMessage(userMessage);
  //   const messageToApi = this.currentMessage.trim();
  //   this.currentMessage = '';
  //   const filesToApi = [...this.selectedFiles];
    
  //   if (this.chatApiService.sessionId) {
  //     formData.append('session_id', this.chatApiService.sessionId);
  //   }
  //   formData.append('text_message', messageToApi);
  //   formData.append('user_id', "user");
  //   formData.append('app_name', "pvm_agent");

  //   if(tableData) formData.append('tableData', JSON.stringify(tableData));
  //   if(jsonData) formData.append('jsonData', JSON.stringify(jsonData));
  //   if (filesToApi) {
  //     for (let i = 0; i < filesToApi.length; i++) {
  //       formData.append('csv_file', filesToApi[i]);
  //     }
  //   }

  //   this.chatStateService.selectedFiles = [];
  //   this.chatStateService.isLoading = true;
  //   const turnStartIndex = this.messages.length;

  //   this.chatApiService.sendMessage(formData).then(observable => {
  //     let agentMessage: Message | undefined;
  //     const apiSub = observable.subscribe({
  //       next: (response: any) => {
  //         console.log("API Response:", response);

  //         if (response.action_type === 'show_progress_bar' && response.data?.job_id) {
  //           const progressMessage: Message = {
  //             text: response.text || 'Processing...',
  //             sender: 'agent',
  //             action_type: 'show_progress_bar',
  //             progress: 0,
  //             job_id: response.data.job_id
  //           };
  //           this.chatStateService.addMessage(progressMessage);
  //           const progressInterval = setInterval(async () => {
  //             try {
  //               const statusRes = await this.chatApiService.getJobStatus(progressMessage.job_id!);
  //               if (statusRes && typeof statusRes.progress === 'number') {
  //                 progressMessage.progress = statusRes.progress;
  //                 if (statusRes.text) {
  //                   progressMessage.text = statusRes.text;
  //                 }
  //                 const newMessages = [...this.messages];
  //                 const index = newMessages.findIndex(m => m === progressMessage);
  //                 if (index > -1) {
  //                   newMessages[index] = progressMessage;
  //                   this.chatStateService.messages = newMessages;
  //                 }
  //                 if (statusRes.progress >= 100) {
  //                   progressMessage.text = "Successfully updated the price for 10 records✅";
  //                   clearInterval(progressInterval);
  //                 }
  //               }
  //             } catch (err) {
  //               console.error('Error fetching job status:', err);
  //               clearInterval(progressInterval);
  //             }
  //           }, 1000);
  //         } else if (response.log?.num_images_found > 0 && Array.isArray(response.steps)) {
  //           const formattedSteps = response.steps.map((s: any, i: number) => ({
  //             step: s.step || i + 1,
  //             text: s.text || '',
  //             has_image: s.has_image || false,
  //             image_urls: s.image_urls || []
  //           }));
  //           agentMessage = {
  //             sender: 'agent',
  //             type: 'steps',
  //             text: formattedSteps[0]?.text || response.answer || '',
  //             formattedText: this.formatText(formattedSteps[0]?.text || response.answer || ''),
  //             steps: formattedSteps,
  //             isStreaming: true
  //           };
  //           this.chatStateService.addMessage(agentMessage);
  //         } else if(response.response) {
  //           const answerText = response.output ;
  //           agentMessage = {
  //             text: answerText,
  //             sender: 'agent',
  //             formattedText: this.formatText(answerText),
  //             isStreaming: true
  //           };
  //           this.chatStateService.addMessage(agentMessage);
  //         }
  //       },
  //       error: (error: any) => {
  //         const errorMessage = error.message || error.error?.message || 'Sorry, something went wrong.';
  //         const newMessages = this.messages.slice(0, turnStartIndex);
  //         this.chatStateService.messages = newMessages;
  //         this.chatStateService.addMessage({ text: errorMessage, sender: 'agent', formattedText: errorMessage });
  //         this.chatStateService.isLoading = false;
  //       },
  //       complete: () => {
  //         if (agentMessage) {
  //           agentMessage.isStreaming = false;
  //           const newMessages = [...this.messages];
  //           const index = newMessages.findIndex(m => m === agentMessage);
  //           if (index > -1) {
  //             newMessages[index] = agentMessage;
  //             this.chatStateService.messages = newMessages;
  //           }
  //         }
  //         this.chatStateService.isLoading = false;
  //       }
  //     });
  //     this.subscriptions.add(apiSub);
  //   });
  // }


  sendMessage(): void {
  if ((!this.currentMessage.trim() && this.selectedFiles.length === 0) || this.isLoading) {
    return;
  }

  const formData = new FormData();

  let tableData: any[] | undefined;
  if (this.dynamicEditTable && this.dynamicEditTable.isTableEditable()) {
    tableData = this.dynamicEditTable.getTableData();
    const tableMessageIndex = this.messages.map(m => m.type).lastIndexOf('table');
    if (tableMessageIndex > -1) {
      const newMessages = [...this.messages];
      newMessages.splice(tableMessageIndex, 1);
      this.chatStateService.messages = newMessages;
    }
  }

  let jsonData: any | undefined;
  const jsonMessageIndex = this.messages.map(m => m.type).lastIndexOf('json');
  if (jsonMessageIndex > -1) {
    const jsonMessage = this.messages[jsonMessageIndex];
    try {
      if (jsonMessage.rawJson) {
        jsonData = JSON.parse(jsonMessage.rawJson);
      }
      const newMessages = [...this.messages];
      newMessages.splice(jsonMessageIndex, 1);
      this.chatStateService.messages = newMessages;
    } catch (e) {
      this.chatStateService.addMessage({ text: 'Error: Invalid JSON format. Fix before sending.', sender: 'agent' });
      return;
    }
  }

  const userMessage: Message = {
    text: this.currentMessage,
    sender: 'user',
    files: [...this.selectedFiles],
  };

  this.chatStateService.addMessage(userMessage);

  const messageToApi = this.currentMessage.trim();
  this.currentMessage = '';
  const filesToApi = [...this.selectedFiles];

  if (this.chatApiService.sessionId) {
    formData.append('session_id', this.chatApiService.sessionId);
  }

  formData.append('text_message', messageToApi);
  formData.append('user_id', 'user');
  formData.append('app_name', 'pvm_agent');

  if (tableData) formData.append('tableData', JSON.stringify(tableData));
  if (jsonData) formData.append('jsonData', JSON.stringify(jsonData));

  if (filesToApi.length) {
    for (let file of filesToApi) {
      formData.append('csv_file', file);
    }
  }

  this.chatStateService.selectedFiles = [];
  this.chatStateService.isLoading = true;
  const turnStartIndex = this.messages.length;

  this.chatApiService.sendMessage(formData).then(observable => {
    let agentMessage: Message | undefined;

    const apiSub = observable.subscribe({
      next: (response: any) => {
        console.log('API Response:', response);

        // 🟡 Progress Bar Response
        if (response.action_type === 'show_progress_bar' && response.data?.job_id) {
          const progressMessage: Message = {
            text: response.text || 'Processing...',
            sender: 'agent',
            action_type: 'show_progress_bar',
            progress: 0,
            job_id: response.data.job_id
          };

          this.chatStateService.addMessage(progressMessage);

          const progressInterval = setInterval(async () => {
            try {
              const statusRes = await this.chatApiService.getJobStatus(progressMessage.job_id!);
              if (statusRes && typeof statusRes.progress === 'number') {
                progressMessage.progress = statusRes.progress;
                if (statusRes.text) progressMessage.text = statusRes.text;

                const newMessages = [...this.messages];
                const index = newMessages.findIndex(m => m === progressMessage);
                if (index > -1) {
                  newMessages[index] = progressMessage;
                  this.chatStateService.messages = newMessages;
                }

                if (statusRes.progress >= 100) {
                  progressMessage.text = "Successfully updated the price for 10 records ✅";
                  clearInterval(progressInterval);
                }
              }
            } catch (err) {
              console.error('Error fetching job status:', err);
              clearInterval(progressInterval);
            }
          }, 1000);
        }

        // 🟢 Steps Response
        else if (response.log?.num_images_found > 0 && Array.isArray(response.steps)) {
          const formattedSteps = response.steps.map((s: any, i: number) => ({
            step: s.step || i + 1,
            text: s.text || '',
            has_image: s.has_image || false,
            image_urls: s.image_urls || []
          }));

          agentMessage = {
            sender: 'agent',
            type: 'steps',
            text: formattedSteps[0]?.text || response.output || '',
            formattedText: this.formatText(formattedSteps[0]?.text || response.output || ''),
            steps: formattedSteps,
            isStreaming: true
          };

          this.chatStateService.addMessage(agentMessage);
        }

        // 🔵 Normal Text Response (FIXED HERE)
        else if (response.output || response.text) {
          const answerText = response.output || response.text;

          agentMessage = {
            text: answerText,
            sender: 'agent',
            formattedText: this.formatText(answerText),
            isStreaming: true
          };

          this.chatStateService.addMessage(agentMessage);
        }
      },

      error: (error: any) => {
        const errorMessage = error.message || error.error?.message || 'Sorry, something went wrong.';
        const newMessages = this.messages.slice(0, turnStartIndex);
        this.chatStateService.messages = newMessages;
        this.chatStateService.addMessage({ text: errorMessage, sender: 'agent', formattedText: errorMessage });
        this.chatStateService.isLoading = false;
      },

      complete: () => {
        if (agentMessage) {
          agentMessage.isStreaming = false;
          const newMessages = [...this.messages];
          const index = newMessages.findIndex(m => m === agentMessage);
          if (index > -1) {
            newMessages[index] = agentMessage;
            this.chatStateService.messages = newMessages;
          }
        }
        this.chatStateService.isLoading = false;
      }
    });

    this.subscriptions.add(apiSub);
  });
}


  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    if (element.files) {
      for (let i = 0; i < element.files.length; i++) {
        this.chatStateService.addFile(element.files[i]);
      }
    }
  }

  removeFile(fileToRemove: File): void {
    this.chatStateService.removeFile(fileToRemove);
  }
  
  onOptionSelected(option: string): void {
    this.currentMessage = option;
    this.sendMessage();
    const optionMessageIndex = this.messages.map(m => m.type).lastIndexOf('option');
    if (optionMessageIndex > -1) {
      const newMessages = [...this.messages];
      newMessages.splice(optionMessageIndex, 1);
      this.chatStateService.messages = newMessages;
    }
  }

  copyJson(msg: Message): void {
    if (msg.rawJson) {
      navigator.clipboard.writeText(msg.rawJson).then(() => {
        msg.copied = true;
        setTimeout(() => {
          msg.copied = false;
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy JSON: ', err);
      });
    }
  }
}
